import { CELL_WIDTH } from "./trackerCell";
import { MAX_INSTRUMENT_SLOT_COUNT } from "../../../../src/lib/audio";

export type SelectionBounds = {
  minStep: number;
  maxStep: number;
  minFlat: number;
  maxFlat: number;
};

export type SelectionTools =
  | {
      ok: true;
      /** Selected column count. */
      width: number;
      bounds: SelectionBounds;
      /** Top-row glyphs as-is (notes, hex, dots, …). */
      startGlyphs: string;
      /** Parsed top-row hex when the slice is all hex digits. */
      start?: number;
      /** Parsed bottom-row hex when valid. */
      end?: number;
      /** Stamp top glyphs onto every selected row. */
      canFillTop: true;
      /** Lerp top → bottom (same-track hex endpoints only). */
      canRamp: boolean;
    }
  | { ok: false; reason: string };

/** @deprecated Prefer SelectionTools — hex-only alias kept for older call sites. */
export type HexSelectionTools = SelectionTools;

/** @deprecated Prefer SelectionTools — kept for older call sites. */
export type RampAnalysis =
  | {
      ok: true;
      start: number;
      end: number;
      width: number;
      bounds: SelectionBounds;
    }
  | { ok: false; reason: string };

/** True when min/max flat sit in the same instrument track. */
export function selectionSameTrack(bounds: SelectionBounds): boolean {
  return Math.floor(bounds.minFlat / CELL_WIDTH) === Math.floor(bounds.maxFlat / CELL_WIDTH);
}

/**
 * Parse a row slice as a hex integer. Every glyph must be 0-9A-F (no dots).
 */
export function parseHexGlyphs(raw: string): number | undefined {
  if (!raw || !/^[0-9A-Fa-f]+$/.test(raw)) {
    return undefined;
  }
  return Number.parseInt(raw, 16);
}

export function formatHexGlyphs(value: number, width: number): string {
  const max = Math.max(0, 16 ** width - 1);
  const clamped = Math.min(max, Math.max(0, Math.round(value)));
  return clamped.toString(16).toUpperCase().padStart(width, "0").slice(-width);
}

/**
 * Vertical selection tools: fill-from-top for any ≥2-row range; ramp when the
 * selection stays on one track and both endpoints are hex.
 */
export function analyzeSelectionTools(
  bounds: SelectionBounds,
  readRow: (step: number) => string,
): SelectionTools {
  if (bounds.maxStep <= bounds.minStep) {
    return { ok: false, reason: "need at least two rows" };
  }
  if (bounds.minFlat > bounds.maxFlat) {
    return { ok: false, reason: "empty columns" };
  }
  const minTrack = Math.floor(bounds.minFlat / CELL_WIDTH);
  const maxTrack = Math.floor(bounds.maxFlat / CELL_WIDTH);
  if (minTrack < 0 || maxTrack >= MAX_INSTRUMENT_SLOT_COUNT) {
    return { ok: false, reason: "bad track" };
  }

  const width = bounds.maxFlat - bounds.minFlat + 1;
  const startRaw = readRow(bounds.minStep);
  if (startRaw.length !== width) {
    return { ok: false, reason: "row width mismatch" };
  }

  const start = parseHexGlyphs(startRaw);
  const endRaw = readRow(bounds.maxStep);
  const end = endRaw.length === width ? parseHexGlyphs(endRaw) : undefined;
  const canRamp =
    selectionSameTrack(bounds) && start !== undefined && end !== undefined;

  return {
    ok: true,
    width,
    bounds,
    startGlyphs: startRaw,
    ...(start !== undefined ? { start } : {}),
    ...(end !== undefined ? { end } : {}),
    canFillTop: true,
    canRamp,
  };
}

/**
 * @deprecated Prefer analyzeSelectionTools.
 * Hex column tools: fill-from-top needs only a hex first row; ramp also needs hex last.
 */
export function analyzeHexSelectionTools(
  bounds: SelectionBounds,
  readRow: (step: number) => string,
): HexSelectionTools {
  const tools = analyzeSelectionTools(bounds, readRow);
  if (!tools.ok) {
    return tools;
  }
  // Legacy callers expected hex-only tops; keep that filter here.
  if (tools.start === undefined) {
    return { ok: false, reason: "top row must be hex digits" };
  }
  return tools;
}

/**
 * Detect a vertical hex ramp: same-track columns, ≥2 rows, first/last rows are hex.
 */
export function analyzeSelectionRamp(
  bounds: SelectionBounds,
  readRow: (step: number) => string,
): RampAnalysis {
  const tools = analyzeSelectionTools(bounds, readRow);
  if (!tools.ok || !tools.canRamp || tools.start === undefined || tools.end === undefined) {
    return { ok: false, reason: tools.ok ? "need hex endpoints on one track" : tools.reason };
  }
  return {
    ok: true,
    start: tools.start,
    end: tools.end,
    width: tools.width,
    bounds: tools.bounds,
  };
}

/** Inclusive lerp of hex values across rows (length = maxStep - minStep + 1). */
export function rampHexValues(start: number, end: number, rowCount: number, width: number): string[] {
  const n = Math.max(2, rowCount);
  const out: string[] = [];
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1);
    const value = start + (end - start) * t;
    out.push(formatHexGlyphs(value, width));
  }
  return out;
}

/** Random hex strings (uppercase) for each selected row. */
export function randomHexValues(
  rowCount: number,
  width: number,
  random: () => number = Math.random,
): string[] {
  const n = Math.max(0, rowCount);
  const w = Math.max(1, width);
  const max = 16 ** w;
  const out: string[] = [];
  for (let i = 0; i < n; i++) {
    const value = Math.floor(random() * max);
    out.push(formatHexGlyphs(value, w));
  }
  return out;
}
